import { apiFetch } from "./api";

/**
 * Named working patterns (FEAT-2026-017 phase 2).
 *
 * A shift sits above the work location in the resolution order: an employee
 * assigned one follows its hours and its working week, falling back to the
 * location and then the organisation for anything it does not set.
 */
export interface ShiftDay {
  /** ISO-8601: 1 = Monday ... 7 = Sunday. */
  weekday: number;
  startTime: string;
  endTime: string;
}

export interface Shift {
  id: string;
  code: string;
  name: string;
  gracePeriodMins: number | null;
  minWorkHoursPerDay: number | null;
  isActive: boolean;
  /** A weekday absent here is not worked on this shift. */
  days: ShiftDay[];
  employeeCount: number;
}

export interface ShiftBody {
  code: string;
  name: string;
  gracePeriodMins?: number | null;
  minWorkHoursPerDay?: number | null;
  isActive?: boolean;
  days: ShiftDay[];
}

export const shiftsApi = {
  list: () => apiFetch<Shift[]>("/shifts"),
  create: (body: ShiftBody) => apiFetch<Shift>("/shifts", { method: "POST", body }),
  update: (id: string, body: Partial<ShiftBody>) =>
    apiFetch<Shift>(`/shifts/${id}`, { method: "PATCH", body }),
  /** Employees on the shift fall back to their location's hours. */
  remove: (id: string) =>
    apiFetch<{ id: string; released: number }>(`/shifts/${id}`, { method: "DELETE" }),
};

export const WEEKDAYS = [
  { n: 1, short: "Mon" },
  { n: 2, short: "Tue" },
  { n: 3, short: "Wed" },
  { n: 4, short: "Thu" },
  { n: 5, short: "Fri" },
  { n: 6, short: "Sat" },
  { n: 7, short: "Sun" },
] as const;

/** "Mon-Thu 08:00-17:00 · Fri 08:00-12:30" — collapses runs of identical hours. */
export function describeShift(days: ShiftDay[]): string {
  if (days.length === 0) return "No working days";
  const sorted = [...days].sort((a, b) => a.weekday - b.weekday);
  const groups: Array<{ from: number; to: number; hours: string }> = [];
  for (const d of sorted) {
    const hours = `${d.startTime}-${d.endTime}`;
    const last = groups[groups.length - 1];
    // Only merge genuinely consecutive weekdays, so Mon+Wed never reads "Mon-Wed".
    if (last && last.hours === hours && d.weekday === last.to + 1) last.to = d.weekday;
    else groups.push({ from: d.weekday, to: d.weekday, hours });
  }
  const label = (n: number) => WEEKDAYS.find((w) => w.n === n)?.short ?? String(n);
  return groups
    .map((g) => `${g.from === g.to ? label(g.from) : `${label(g.from)}-${label(g.to)}`} ${g.hours}`)
    .join(" · ");
}
